import { CampaignService } from '@/entities/campaign/api/service';
import { useCampaignStore } from '@/entities/campaign/model';
import type { CampaignId } from '@/entities/campaign/model/campaignStore';
import { VALID_CAMPAIGNS } from '@/entities/campaign/model/campaignStore';
import { TournamentService } from '@/entities/tournament/api/service';
import type { ITournamentActive } from '@/entities/tournament/api/types';
import { Modal, Typography } from '@/shared/ui';
import Loader from '@/shared/ui/Loader';
import TournamentBanner from '@/widgets/TournamentBanner';
import TournamentStartCountdown from '@/widgets/TournamentStartCountdown';
import { useMutation, useQuery } from '@tanstack/react-query';
import { retrieveLaunchParams } from '@telegram-apps/sdk-react';
import { motion } from 'framer-motion';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import styles from './TournamentCampaignPopup.module.scss';

const log = console.log.bind(console, '[Tournament Campaign Popup]: ');

type TournamentWithParticipation = ITournamentActive & { isParticipant: boolean };
interface TournamentCampaignPopupProps {
	campaignId: CampaignId;
	onCloseClick: () => void;
	onJoinClick: () => void;
}

const TournamentCampaignPopup = ({ campaignId, onJoinClick, onCloseClick }: TournamentCampaignPopupProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'campaign.tournament' });
	const { t: tTournamentSelection } = useTranslation('translation', { keyPrefix: 'tournamentSelection' });
	const navigate = useNavigate();
	const playerId = retrieveLaunchParams().tgWebAppData?.user?.id;
	const { setCampaignModalClosedTime, countCampaignPopupTimesClosed, isCampaignGoalAchieved } = useCampaignStore();

	const fetchClosestNonParticipatedTournament = async () => {
		log('Fetching closest tournament');
		const activeTournaments = await TournamentService.getAllActiveTournaments();

		if (activeTournaments.length === 0) {
			log('No active tournaments');

			return;
		}

		log('There are active tournaments. Fetching participated tournaments');

		let participatedTournaments: { tournamentId: number }[] = [];

		try {
			participatedTournaments = await TournamentService.getTournamentsByPlayerId(playerId);
		} catch (error) {
			log('Failed to fetch participated tournaments, proceeding without filtering by participation', error);
		}

		log('Fetched player participated tournaments ', participatedTournaments);

		const tournamentsWithParticipationFlag: TournamentWithParticipation[] = activeTournaments.map((t) => ({
			...t,
			isParticipant: participatedTournaments.some((pt) => t.id === pt.tournamentId),
		}));

		log('Created an array of tournaments with participation flag', tournamentsWithParticipationFlag);

		const hasParticipation = tournamentsWithParticipationFlag.some((t) => t.isParticipant);

		if (hasParticipation) {
			log('Player has participated in tournaments. Filtering');

			const tournamentsWithoutParticipationSortedByTime = tournamentsWithParticipationFlag
				.filter((t) => !t.isParticipant)
				.filter((t) => new Date(t.startAt).getTime() > Date.now())
				.sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());

			log('Sorted tournaments without participation by start date ', tournamentsWithoutParticipationSortedByTime);

			const resultTournament =
				tournamentsWithoutParticipationSortedByTime.length > 0
					? tournamentsWithoutParticipationSortedByTime[0]
					: activeTournaments.find(
							(t) =>
								t.type === 'participants' &&
								tournamentsWithParticipationFlag.some((pt) => !pt.isParticipant && pt.id === t.id),
					  );

			log('Result tournament is ', resultTournament);

			if (resultTournament) {
				log('Set closest tournament without participation:', resultTournament);

				return resultTournament;
			}
		} else {
			log('Player has not participated in any tournament yet. Returning the closest one');

			const sortedTournamentsByStart = activeTournaments
				.filter((t) => new Date(t.startAt).getTime() > Date.now())
				.sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());

			log('Sorted by date');

			const resultTournament =
				sortedTournamentsByStart.length > 0
					? sortedTournamentsByStart[0]
					: activeTournaments.find(
							(t) =>
								t.type === 'participants' &&
								tournamentsWithParticipationFlag.some((pt) => pt.id === t.id && !pt.isParticipant),
					  );

			log('Result tournament in the end is -', resultTournament);

			if (resultTournament) {
				log('Set closest tournament without participation:', resultTournament);

				return resultTournament;
			}
		}
	};

	const { data, isLoading } = useQuery({
		queryFn: async () => await fetchClosestNonParticipatedTournament(),
		queryKey: ['TournamentCampaignPopupClosestTournament'],
	});

	const { data: participantsCount } = useQuery({
		enabled: !!data && data.type === 'participants',
		queryFn: async () => {
			const players = await TournamentService.getAllPlayersByTournament(data?.id);

			return players.length;
		},
		queryKey: [TournamentService.getAllPlayersByTournament.queryKey, data?.id],
	});

	const location = useLocation();

	const isOnTournamentPage =
		location.pathname.startsWith('/tournament-overview') ||
		location.pathname.startsWith('/tournament-selection') ||
		location.pathname.includes('/tournament');

	const { mutate: sendCampaignJoinPopup } = useMutation({
		mutationFn: () => CampaignService.sendCampaignJoinPopup(),
		onError: (error) => {
			console.error('Error after sending popup join postback', error);
		},

		onSuccess: () => {
			log('Popup join postback successfully sent');
		},
		retry: 3,
		retryDelay: 3000,
	});

	const { mutate: sendCampaignClosePopup } = useMutation({
		mutationFn: () => CampaignService.sendCampaignClosePopup(),
		onError: (error) => {
			console.error('Error after sending popup close postback', error);
		},

		onSuccess: () => {
			log('Popup close postback successfully sent');
		},
		retry: 3,
		retryDelay: 3000,
	});

	if (isOnTournamentPage || isCampaignGoalAchieved) return null;

	return (
		<motion.div
			initial={{ opacity: 0, y: 10 }}
			animate={{ opacity: 1, y: 0 }}
			exit={{ opacity: 0, y: -10 }}
			transition={{ duration: 0.4, ease: 'easeInOut' }}
			className={styles.container}
		>
			<div className={styles.overlay} />
			<div className={styles.contentWrapper}>
				{isLoading && <Loader />}
				{data && !isLoading && (
					<Modal
						isLight
						textButton={t('joinBtn', { currency: '$', price: 1 })}
						externalButton={t('maybeLaterBtn')}
						externalButtonOnClick={() => {
							onCloseClick();
							sendCampaignClosePopup();
							setCampaignModalClosedTime(Date.now());
						}}
						onClick={() => {
							if (campaignId === VALID_CAMPAIGNS[0]) sendCampaignJoinPopup();
							onJoinClick();
							setCampaignModalClosedTime(Date.now());

							if (data) {
								navigate(`/tournament-overview/${data.id}`);

								return;
							}

							navigate('/tournament-selection');
						}}
					>
						<div className={styles.content}>
							<div className={styles.tournamentBanner}>
								<TournamentBanner tournamentId={data.id} />
							</div>
							{data.type === 'time' && (
								<TournamentStartCountdown initialTimeSeconds={(new Date(data.startAt).getTime() - Date.now()) / 1000} />
							)}
							{data.type === 'participants' && (
								<div className={styles.countParticipant}>
									<Typography variant='body3' color='white'>
										{tTournamentSelection('pendingParticipants', {
											currentCount: participantsCount,
											maxCount: data.maxParticipants,
										})}
									</Typography>
								</div>
							)}
							<Typography variant='headline2' color='white' fontWeight={600}>
								{countCampaignPopupTimesClosed > 0 ? t('infoAfterPlaying') : t('information')}
							</Typography>
						</div>
					</Modal>
				)}
			</div>
		</motion.div>
	);
};

export default TournamentCampaignPopup;
